import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard,
  FileSearch,
  Users,
  CreditCard,
  Settings,
  Sparkles,
  BarChart3,
  FolderOpen,
  Clock,
  X,
  FileText
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { UnifiedCreditService, type UnifiedCreditData } from '../../lib/unified-credit-service';
import { DualCreditDisplay } from '../credits/DualCreditDisplay';
import { subscribeToCreditChanges } from '../../lib/credit-events';

const getMenuItems = (isAdmin: boolean) => {
  const items = [
    { 
      icon: LayoutDashboard, 
      label: 'Dashboard', 
      path: '/dashboard',
      description: 'Overview and analytics'
    },
    { 
      icon: Sparkles, 
      label: 'Ad Generator', 
      path: '/dashboard/ad-generator',
      description: 'Create ads with AI'
    },
    { 
      icon: FileSearch, 
      label: 'Analysis', 
      path: '/dashboard/analysis',
      description: 'Check creative compliance'
    },
    { 
      icon: Clock, 
      label: 'Recent Analyses', 
      path: '/dashboard/recent-analyses',
      description: 'View analysis history'
    },
    { 
      icon: BarChart3, 
      label: 'Reports', 
      path: '/dashboard/reports',
      description: 'View detailed reports'
    },
    { 
      icon: FolderOpen, 
      label: 'Projects', 
      path: '/dashboard/projects',
      description: 'Manage your projects'
    },
    { 
      icon: Users, 
      label: 'Team', 
      path: '/dashboard/team',
      description: 'Collaborate with team'
    },
    { 
      icon: CreditCard, 
      label: 'Credits', 
      path: '/dashboard/credits',
      description: 'Manage subscription'
    }
  ];

  if (isAdmin) {
    items.push({
      icon: FileText,
      label: 'Blog',
      path: '/dashboard/blog',
      description: 'Manage blog posts'
    });
  }

  items.push({
    icon: Settings, 
    label: 'Settings', 
    path: '/dashboard/settings',
    description: 'Account preferences'
  });

  return items;
};

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  isMobile?: boolean;
}

export function Sidebar({ isOpen = true, onClose, isMobile = false }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [creditData, setCreditData] = React.useState<UnifiedCreditData | null>(null);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = React.useState(false);
  const navRef = React.useRef<HTMLElement>(null);

  // Load credit data and check admin status
  React.useEffect(() => {
    const loadCreditData = async () => {
      if (!user?.id) return;
      try {
        const data = await UnifiedCreditService.getCreditData(user.id);
        setCreditData(data);
      } catch (error) {
        console.error('Failed to load credit data in sidebar:', error);
      }
    };

    loadCreditData();
    
    // Check if user is system admin (for blog management)
    if (user?.system_role === 'super_admin' || user?.system_role === 'content_editor') {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  // Listen for real-time credit changes
  React.useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = subscribeToCreditChanges(user.id, (event) => {
      // Refresh credit data when credits change
      UnifiedCreditService.getCreditData(user.id).then(setCreditData).catch(console.error);
    });

    return unsubscribe;
  }, [user]);

  // Handle scroll indicator visibility
  React.useEffect(() => {
    const handleScroll = () => {
      if (!navRef.current) return;
      
      const { scrollTop, scrollHeight, clientHeight } = navRef.current;
      const isScrollable = scrollHeight > clientHeight;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px buffer
      
      setShowScrollIndicator(isScrollable && !isAtBottom);
    };

    const navElement = navRef.current;
    if (navElement) {
      // Check initially
      handleScroll();
      
      // Add scroll listener
      navElement.addEventListener('scroll', handleScroll);
      
      // Also check on resize
      const resizeObserver = new ResizeObserver(handleScroll);
      resizeObserver.observe(navElement);
      
      return () => {
        navElement.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [isAdmin]); // Re-run when admin status changes (affects menu items)

  const handleNavigation = (path: string) => {
    navigate(path);
    // Close sidebar on mobile after navigation
    if (isMobile && onClose) {
      onClose();
    }
  };

  const sidebarContent = (
    <div className="w-64 bg-white border-r border-gray-200 h-full flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center">
              <img 
                src="/Logo-checkmark-sidebar.svg" 
                alt="Rulevia Logo" 
                className="w-10 h-10"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Rulevia</h1>
              <p className="text-xs text-gray-500 hidden sm:block">Creative Compliance</p>
            </div>
          </div>
          {/* Close button for mobile */}
          {isMobile && onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </div>
      </div>

      {/* Credits Display - Mobile only */}
      {isMobile && creditData && creditData.adCreative && (
        <div className="px-4 pb-4">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-900">Credits</h3>
              <CreditCard className="w-4 h-4 text-purple-600" />
            </div>
            <DualCreditDisplay
              analysisCredits={creditData.availableCredits}
              adCreativeCredits={creditData.adCreative.availableCredits}
              size="small"
              orientation="vertical"
              className="text-xs"
            />
            <button
              onClick={() => handleNavigation('/dashboard/credits')}
              className="mt-2 text-xs text-purple-600 hover:text-purple-700 font-medium"
            >
              Manage Plan →
            </button>
          </div>
        </div>
      )}

      {/* Navigation with scroll indicator */}
      <div className="flex-1 flex flex-col relative min-h-0">
        <nav 
          ref={navRef}
          className="flex-1 p-4 space-y-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400 min-h-0"
        >
          {getMenuItems(isAdmin).map((item) => {
            const isActive = location.pathname === item.path;
            
            // Add data-tutorial attribute for tutorial targeting
            const getTutorialAttribute = (label: string) => {
              switch(label) {
                case 'Analysis': return 'analysis';
                case 'Projects': return 'projects';
                case 'Reports': return 'reports';
                default: return undefined;
              }
            };
            
            return (
              <motion.button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                data-tutorial={getTutorialAttribute(item.label)}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all group ${
                  isActive
                    ? 'bg-gradient-to-r from-purple-50 to-blue-50 text-purple-700 border border-purple-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                whileHover={{ x: 2 }}
                whileTap={{ scale: 0.98 }}
              >
                <item.icon className={`w-5 h-5 ${isActive ? 'text-purple-600' : 'text-gray-500 group-hover:text-gray-700'}`} />
                <div className="flex-1 text-left">
                  <div className={`font-medium ${isActive ? 'text-purple-900' : 'text-gray-700 group-hover:text-gray-900'}`}>
                    {item.label}
                  </div>
                  <div className={`text-xs ${isActive ? 'text-purple-600' : 'text-gray-500'} hidden sm:block`}>
                    {item.description}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </nav>
        
        {/* Scroll indicator */}
        <AnimatePresence>
          {showScrollIndicator && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none bg-gradient-to-t from-white via-white/80 to-transparent"
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );

  // Mobile drawer with overlay
  if (isMobile) {
    return (
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            />
            {/* Sliding sidebar */}
            <motion.div
              initial={{ x: -256 }}
              animate={{ x: 0 }}
              exit={{ x: -256 }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed top-0 left-0 z-50 lg:hidden h-screen"
            >
              {sidebarContent}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }

  // Desktop sidebar
  if (!isMobile && isOpen) {
    return (
      <div className="hidden lg:block sticky top-0 h-screen">
        {sidebarContent}
      </div>
    );
  }

  // Don't render anything if desktop and closed, or mobile (mobile is handled above)
  return null;
}